import React, { useEffect, useState } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import api from '../../utils/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const EditDeleteDownloadDrawer = ({ isOpen = false, onClose, download, onUpdated, onDeleted }) => {
  const [form, setForm] = useState({
    name: '',
    description: '',
    version: '',
    slug: '',
    downloader_code: '',
    downloader_url: '',
    category_id: '',
    is_visible: 'active',
    status: 'publish',
  });
  const [tagsArray, setTagsArray] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [file, setFile] = useState(null);
  const [currentFile, setCurrentFile] = useState(null);
  const [removeFile, setRemoveFile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, false] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: ['', 'center', 'right', 'justify'] }],
      [{ color: [] }],
      ['clean'],
    ],
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await api.get('/api/categories');
        setCategories(res.data);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError('Failed to load categories');
      }
    };
    if (isOpen) fetchCategories();
  }, [isOpen]);

  useEffect(() => {
    if (download) {
      console.log('Download data:', download);
      setForm({
        name: download.name || '',
        description: download.description || '',
        version: download.version || '',
        slug: download.slug || '',
        downloader_code: download.downloader_code || '',
        downloader_url: download.downloader_url || '',
        category_id: download.category_id || '',
        is_visible: ['active', 'inactive'].includes(download.is_visible) ? download.is_visible : 'active',
        status: ['publish', 'draft'].includes(download.status) ? download.status : 'publish',
      });
      setTagsArray(download.tags ? download.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : []);
      setCurrentFile(download.file_path || null);
      setFile(null);
      setRemoveFile(false);
      setError('');
      setShowDeleteConfirm(false);
    }
  }, [download]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDescriptionChange = (value) => {
    setForm((prev) => ({ ...prev, description: value }));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmed = tagInput.trim();
      if (trimmed && !tagsArray.includes(trimmed)) {
        setTagsArray((prev) => [...prev, trimmed]);
      }
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setTagsArray((prev) => prev.filter((tag) => tag !== tagToRemove));
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setRemoveFile(false); // Reset removeFile when a new file is selected
  };

  const handleRemoveFile = () => {
    setFile(null);
    setRemoveFile(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug || !form.downloader_code) {
      setError('Required fields: Name, Slug, and Downloader Code');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const data = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (key === 'category_id' && value === '') {
          // Omit empty category_id
        } else {
          data.append(key, value);
        }
      });
      data.append('tags', tagsArray.join(','));
      if (file) data.append('file', file);
      if (removeFile) data.append('remove_file', 'true');

      const formDataEntries = {};
      for (let [key, value] of data.entries()) {
        formDataEntries[key] = value instanceof File ? { name: value.name, size: value.size, type: value.type } : value;
      }
      console.log('Enviando FormData:', formDataEntries);

      await api.put(`/api/downloads/${download.id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      onUpdated?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating download');
      console.error('Error updating download:', err, {
        response: err.response?.data,
        status: err.response?.status,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setError('');
    try {
      await api.delete(`/api/downloads/${download.id}`, { timeout: 30000 });
      onDeleted?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting download');
      console.error('Error deleting download:', err, {
        response: err.response?.data,
        status: err.response?.status,
      });
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  if (!download) return null;
  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-y-0 right-0 max-w-full flex sm:w-full sm:max-w-[800px]">
        <DialogPanel className="w-full bg-white shadow-2xl rounded-l-lg flex flex-col h-screen sm:rounded-r-lg">
          <div className="p-6 overflow-y-auto flex-1 space-y-6">
            <div>
              <DialogTitle className="text-2xl font-bold text-gray-900">Edit Download</DialogTitle>
              <p className="mt-2 text-sm text-gray-600">Update the download details. Fields marked with * are required.</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">Name *</label>
                <p className="text-xs text-gray-500 mt-1">The name of the download.</p>
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Download name"
                  className="mt-1 block w-full border border-gray-200 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
                <p className="text-xs text-gray-500 mt-1">The description of the download.</p>
                <div className="mt-1">
                  <ReactQuill
                    id="description"
                    theme="snow"
                    value={form.description}
                    onChange={handleDescriptionChange}
                    modules={quillModules}
                    className="border border-gray-200 rounded-md shadow-sm focus-within:ring-blue-500 focus-within:border-blue-500 sm:text-sm"
                    placeholder="Download description"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="version" className="block text-sm font-medium text-gray-700">Version</label>
                <p className="text-xs text-gray-500 mt-1">The version of the download.</p>
                <input
                  id="version"
                  type="text"
                  name="version"
                  value={form.version}
                  onChange={handleChange}
                  placeholder="1.0.0"
                  className="mt-1 block w-full border border-gray-200 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="slug" className="block text-sm font-medium text-gray-700">Slug *</label>
                <p className="text-xs text-gray-500 mt-1">The unique identifier for the download URL.</p>
                <input
                  id="slug"
                  type="text"
                  name="slug"
                  value={form.slug}
                  onChange={handleChange}
                  placeholder="download-slug"
                  className="mt-1 block w-full border border-gray-200 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label htmlFor="downloader_code" className="block text-sm font-medium text-gray-700">Downloader Code *</label>
                <p className="text-xs text-gray-500 mt-1">The code required to access the download.</p>
                <input
                  id="downloader_code"
                  type="text"
                  name="downloader_code"
                  value={form.downloader_code}
                  onChange={handleChange}
                  placeholder="Code"
                  className="mt-1 block w-full border border-gray-200 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label htmlFor="downloader_url" className="block text-sm font-medium text-gray-700">Downloader URL</label>
                <p className="text-xs text-gray-500 mt-1">The URL for downloading the file.</p>
                <input
                  id="downloader_url"
                  type="url"
                  name="downloader_url"
                  value={form.downloader_url}
                  onChange={handleChange}
                  placeholder="https://example.com/download"
                  className="mt-1 block w-full border border-gray-200 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="file" className="block text-sm font-medium text-gray-700">File</label>
                <p className="text-xs text-gray-500 mt-1">Current file or select a new one to replace it (optional).</p>
                {currentFile && !removeFile && !file ? (
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-sm text-gray-600">{currentFile.split('/').pop()}</span>
                    <button
                      type="button"
                      onClick={handleRemoveFile}
                      className="text-sm text-red-600 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                ) : file ? (
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-sm text-gray-600">New file: {file.name}</span>
                    <button
                      type="button"
                      onClick={() => setFile(null)}
                      className="text-sm text-red-600 hover:underline"
                    >
                      Clear
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No file associated</p>
                )}
                <input
                  id="file"
                  type="file"
                  onChange={handleFileChange}
                  className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border file:border-gray-200 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>
              <div>
                <label htmlFor="category_id" className="block text-sm font-medium text-gray-700">Category</label>
                <p className="text-xs text-gray-500 mt-1">Select the category for the download.</p>
                <select
                  id="category_id"
                  name="category_id"
                  value={form.category_id}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-200 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">Select a category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="tags" className="block text-sm font-medium text-gray-700">Tags</label>
                <p className="text-xs text-gray-500 mt-1">Add tags to categorize the download (press Enter to add).</p>
                <input
                  id="tags"
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  placeholder="Type a tag and press Enter"
                  className="mt-1 block w-full border border-gray-200 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
                <div className="mt-2 flex flex-wrap gap-2">
                  {tagsArray.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-1 text-blue-600 hover:text-blue-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <label htmlFor="is_visible" className="block text-sm font-medium text-gray-700">Download Visibility</label>
                  <p className="text-xs text-gray-500 mt-1">Toggle to make the download visible to clients.</p>
                </div>
                <div className="flex items-center">
                  <input
                    id="is_visible"
                    name="is_visible"
                    type="checkbox"
                    checked={form.is_visible === 'active'}
                    onChange={() => setForm((prev) => ({ ...prev, is_visible: prev.is_visible === 'active' ? 'inactive' : 'active' }))}
                    className="sr-only"
                  />
                  <button
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, is_visible: prev.is_visible === 'active' ? 'inactive' : 'active' }))}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${form.is_visible === 'active' ? 'bg-blue-600' : 'bg-gray-200'}`}
                    role="switch"
                    aria-checked={form.is_visible === 'active'}
                    aria-labelledby="is_visible"
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${form.is_visible === 'active' ? 'translate-x-5' : 'translate-x-0'}`}
                    />
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700">Status</label>
                <p className="text-xs text-gray-500 mt-1">Define the current status of the download.</p>
                <select
                  id="status"
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-200 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="publish">Publish</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
            </form>
            <div className="mt-10">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Download</h3>
              <p className="text-sm text-gray-600 mb-4">Permanently delete this download from the system. This action cannot be undone.</p>
              <div className="flex items-center gap-4 border border-red-200 bg-red-50 p-4 rounded">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-500" aria-hidden="true" />
                <div className="flex-1">
                  <p className="text-sm text-red-600">Confirm deletion of the download "{form.name}".</p>
                </div>
                {showDeleteConfirm ? (
                  <button
                    onClick={handleDelete}
                    disabled={loading}
                    className="text-sm text-white bg-red-600 hover:bg-red-700 px-3 py-1 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    {loading ? 'Deleting...' : 'Confirm Deletion'}
                  </button>
                ) : (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="text-sm text-red-600 hover:underline focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    Delete Download
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="bg-gray-50 border-t px-6 py-4 flex justify-between gap-4 sticky bottom-0">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            >
              Close
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default EditDeleteDownloadDrawer;